import type { CompanyVideo } from "@/modules/companies/api/types"
import type { FunctionComponent } from "react"

import { clsx } from "clsx"
import dynamic from "next/dynamic"
import Image from "next/image"
import { useCallback, useState } from "react"

import { Text } from "@jobteaser/spark/components/Text"

import { MediaImage } from "@/features/companyDetail/components/shared/Media/Image"
import { getPlayIconProps } from "@/features/companyDetail/components/shared/Media/utils/getPlayIconProps"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { useTracking } from "@/modules/tracking/useTracking"

import styles from "./Media.module.css"

const VideoPlayerDynamic = dynamic(
  () => import("@/modules/design/components/VideoPlayer/VideoPlayer").then(mod => mod.VideoPlayer),
  { ssr: false }
)

export type MediaVideoTracking = {
  id?: string
  name?: string
}

export type MediaVideoProps = {
  className?: string
  onStart?: () => void
  priority?: boolean
  tracking?: MediaVideoTracking
  video: CompanyVideo
  withDescription?: boolean
}

export const MediaVideo: FunctionComponent<MediaVideoProps> = ({
  className,
  onStart,
  priority,
  tracking: trackingData,
  video,
  withDescription = true,
}) => {
  const locale = useLocale()
  const tracking = useTracking()
  const { t } = useTranslation(["companies_client"])

  const { description, previewUrl, url } = video

  const isVideoWithThumbnail = !!previewUrl
  const [isThumbnailDisplayed, setIsThumbnailDisplayed] = useState(isVideoWithThumbnail)

  const onVideoStart = useCallback(() => {
    tracking?.track("media_video_click", {
      company_id: trackingData?.id || "",
      company_name: trackingData?.name || "",
      service: "company",
      trackee_locale: locale,
      video_id: url,
    })
    return onStart?.()
  }, [tracking, onStart, trackingData, url, locale])

  return (
    <div className={clsx(styles.videoContainer, className)}>
      <div
        className={styles.thumbnail}
        onClick={() => setIsThumbnailDisplayed(false)}
        onKeyDown={() => setIsThumbnailDisplayed(false)}
        role="button"
        tabIndex={0}
      >
        {isThumbnailDisplayed && (
          <>
            <Image
              alt={t("companies_client.media.video.iconPlayerAlt")}
              {...getPlayIconProps(video.url)}
              height={48}
              priority={priority}
              width={68}
            />
            <MediaImage
              alt={t("companies_client.media.video.thumbnailAlt")}
              aria-labelledby={description}
              className={styles.thumbnailImage}
              priority={priority}
              src={previewUrl}
              wrapperClassName={styles.thumbnailImageWrapper}
            />
          </>
        )}
        {!isThumbnailDisplayed && (
          <div data-testid="company_information_video">
            <VideoPlayerDynamic onStart={onVideoStart} playing={isVideoWithThumbnail} url={url} />
          </div>
        )}
      </div>
      {withDescription && description && (
        <div className={styles.note} id={description}>
          <Text variant="body1">{description}</Text>
        </div>
      )}
    </div>
  )
}
